import React, { useCallback, useMemo } from 'react';
import { Button, Icon, Link, Text, Tooltip } from '@nimbus-ds/components';
import { CalendarIcon, HistoryIcon } from '@nimbus-ds/icons';
import { Country } from 'App/i18n/enums';
import { MoneyCard } from 'commons/components';
import FeatureDiscoveryPopover from 'commons/components/FeatureDiscoveryPopover/FeatureDiscoveryPopover';
import { useIsMobile } from 'commons/hooks';
import { FeatureName } from 'commons/services/featureDiscoveryService/featureDiscoveryServiceResponses';
import { MoneyObjectInterface } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';

interface AvailableToWithdrawCardProps {
  availableToWithdraw: MoneyObjectInterface;
  loading: boolean;
  showLoadingDelay: boolean;
  isError: boolean;
  showSupportTooltip: boolean;
  transferFlagDisabled?: boolean;
  handleTransferClick: () => void;
  disabled: boolean;
  automatedWithdrawEnabled?: boolean;
  thereArePendingPayments?: boolean;
  partialWithdrawEnabled?: boolean;
  transferDisableReason?: string;
}

function AvailableToWithdrawCard({
  availableToWithdraw,
  loading,
  showLoadingDelay,
  isError,
  showSupportTooltip,
  transferFlagDisabled = false,
  handleTransferClick,
  disabled,
  automatedWithdrawEnabled = false,
  thereArePendingPayments = false,
  partialWithdrawEnabled = false,
  transferDisableReason,
}: AvailableToWithdrawCardProps): JSX.Element {
  const { t } = useTranslationWithPrefix('home.availableToWithdrawCard');
  const { t: tHome } = useTranslationWithPrefix('home');
  const { t: tTransfer } = useTranslationWithPrefix('transfer');
  const isMobile = useIsMobile();

  const shouldShowTransferTooltip = useMemo(
    () => transferFlagDisabled || Boolean(transferDisableReason),
    [transferFlagDisabled, transferDisableReason],
  );
  const shouldPreventDefault = shouldShowTransferTooltip;

  const TransferAction = useCallback(
    ({ children }: { children: JSX.Element }) => {
      if (!shouldShowTransferTooltip) return <>{children}</>;

      const content = transferFlagDisabled
        ? tTransfer('transferDisabledTooltip')
        : transferDisableReason!;

      return (
        <Tooltip
          data-testid="blocked-tooltip"
          content={content}
          arrow
          maxWidth={isMobile ? '160px' : undefined}
        >
          {children}
        </Tooltip>
      );
    },
    [
      shouldShowTransferTooltip,
      transferFlagDisabled,
      transferDisableReason,
      tTransfer,
      isMobile,
    ],
  );

  const getPrimaryButton = () => {
    if (showSupportTooltip) {
      return (
        <Tooltip content={t('supportTooltip')}>
          <Button
            appearance="neutral"
            onClick={handleTransferClick}
            disabled={disabled}
          >
            <Text>{t('transferButton')}</Text>
          </Button>
        </Tooltip>
      );
    }
    if (thereArePendingPayments) {
      const link = (
        <Link
          as="button"
          textDecoration="none"
          onClick={
            shouldPreventDefault
              ? (e) => e.preventDefault()
              : handleTransferClick
          }
          data-testid="pendingPaymentsLink"
        >
          <Icon source={<HistoryIcon />} />
          <Text>{t('pendingPaymentsLink')}</Text>
        </Link>
      );
      return <TransferAction>{link}</TransferAction>;
    }
    if (automatedWithdrawEnabled) {
      const link = (
        <Link
          as="button"
          textDecoration="none"
          onClick={
            shouldPreventDefault
              ? (e) => e.preventDefault()
              : handleTransferClick
          }
          disabled={disabled}
          data-testid="scheduledWithdrawalsButton"
        >
          <Icon source={<CalendarIcon />} />
          <Text>{t('scheduledWithdrawalsButton')}</Text>
        </Link>
      );
      return <TransferAction>{link}</TransferAction>;
    }

    const button = (
      <Button
        appearance="neutral"
        onClick={
          shouldPreventDefault ? (e) => e.preventDefault() : handleTransferClick
        }
        disabled={disabled}
      >
        <Text>{t('transferButton')}</Text>
      </Button>
    );
    return <TransferAction>{button}</TransferAction>;
  };

  const button = getPrimaryButton();
  const isTransferActionable =
    !showSupportTooltip && !transferFlagDisabled && !disabled;
  const primaryButton =
    partialWithdrawEnabled && isTransferActionable ? (
      <FeatureDiscoveryPopover
        featureName={FeatureName.PartialWithdraw}
        title={tHome('partialWithdrawDiscovery.title')}
        description={tHome('partialWithdrawDiscovery.description')}
        linkText={tHome('partialWithdrawDiscovery.dismiss')}
        onLinkClick={() => undefined}
        position="bottom-end"
        onlyForCountries={[Country.ARGENTINA, Country.MEXICO]}
      >
        {button}
      </FeatureDiscoveryPopover>
    ) : (
      button
    );

  return (
    <MoneyCard
      title={t('title')}
      amount={availableToWithdraw}
      skeleton={loading}
      showLoadingDelay={showLoadingDelay}
      loadingText={t('updatingAvailableMoney')}
      hasError={isError}
      color="primary"
      primaryButton={primaryButton}
    />
  );
}

export default AvailableToWithdrawCard;
